// src/services/eventosService.js
import { getAuthHeader } from "./authService";

/** Institución fija (dato quemado) */
export const INSTITUCION_ID = "16020324-f0ea-4319-905c-5a30618ce9f5";

/** Base URL del backend */
const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1/eventos`;

/* ========== Helpers ========== */
const toJson = (res) => res.json().catch(() => ({}));

const ensureOk = (res, data) => {
  if (!res.ok) {
    const msg =
      data?.message ||
      data?.error ||
      (typeof data === "string" ? data : "") ||
      `HTTP ${res.status}`;
    throw new Error(msg);
  }
};

const clean = (obj) => {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    out[k] = v;
  }
  return out;
};

/**
 * GET - Listar todos los eventos de la institución
 * Endpoint: GET /api/eventos/:contextInstitutionId
 * @returns {Promise<Array>} Lista de eventos
 */
export async function listEventos() {
  const res = await fetch(`${BASE_URL}/${INSTITUCION_ID}`, {
    headers: { Accept: "application/json", ...getAuthHeader() },
  });
  const data = await toJson(res);
  ensureOk(res, data);
  return data?.data ?? [];
}

/**
 * GET - Obtener eventos filtrados con parámetros opcionales
 * Endpoint: GET /api/eventos/filtered/:contextInstitutionId
 * @param {Object} filters - Filtros opcionales
 * @param {string} [filters.tipo] - Tipo: 'entrenamiento', 'torneo', 'todos'
 * @param {string} [filters.estado] - Estado: 'programado', 'en_curso', 'finalizado', 'cancelado', 'todos'
 * @param {string} [filters.fecha_inicio] - Fecha inicio rango (YYYY-MM-DD)
 * @param {string} [filters.fecha_fin] - Fecha fin rango (YYYY-MM-DD)
 * @param {number} [filters.grupo_id] - ID del grupo
 * @returns {Promise<Object>} { data: [...], filtros_aplicados: {...} }
 */
export async function listEventosFiltered(filters = {}) {
  const queryParams = new URLSearchParams();
  
  if (filters.tipo) queryParams.set("tipo", filters.tipo);
  if (filters.estado) queryParams.set("estado", filters.estado);
  if (filters.fecha_inicio) queryParams.set("fecha_inicio", filters.fecha_inicio);
  if (filters.fecha_fin) queryParams.set("fecha_fin", filters.fecha_fin);
  if (filters.grupo_id) queryParams.set("grupo_id", String(filters.grupo_id));

  const queryString = queryParams.toString();
  const url = `${BASE_URL}/filtered/${INSTITUCION_ID}${queryString ? `?${queryString}` : ""}`;

  const res = await fetch(url, {
    headers: { Accept: "application/json", ...getAuthHeader() },
  });
  const data = await toJson(res);
  ensureOk(res, data);
  
  return {
    data: data?.data ?? [],
    filtros_aplicados: data?.filtros_aplicados ?? {},
  };
}

/**
 * GET - Obtener fechas, tipos y nombres de eventos activos
 * Endpoint: GET /api/eventos/fechas-tipos/:contextInstitutionId
 * @returns {Promise<Array>} Lista simplificada de eventos activos ordenados por fecha_inicio
 * @example
 * [
 *   {
 *     id: "a92220cb-0a83-4086-b292-1aa4445651e1",
 *     fecha_inicio: "2025-11-10T15:00:00+00:00",
 *     tipo: "entrenamiento",
 *     nombre: "Entrenamiento de técnica"
 *   }
 * ]
 */
export async function listEventosActivosFechasTipos() {
  const res = await fetch(`${BASE_URL}/fechas-tipos/${INSTITUCION_ID}`, {
    headers: { Accept: "application/json", ...getAuthHeader() },
  });
  const data = await toJson(res);
  ensureOk(res, data);
  return data?.data ?? [];
}

/**
 * POST - Crear un nuevo evento
 * Endpoint: POST /api/eventos/:contextInstitutionId
 * @param {Object} form - Datos del evento
 * @param {string} form.nombre - Nombre del evento
 * @param {string} form.tipo - Tipo: 'entrenamiento' o 'torneo'
 * @param {string} form.descripcion - Descripción del evento (se mapea a observaciones)
 * @param {string} form.fecha_inicio - Fecha y hora de inicio (ISO 8601)
 * @param {string} form.fecha_fin - Fecha y hora de fin (ISO 8601)
 * @param {string} form.ubicacion - Ubicación del evento
 * @param {number} form.grupo_id - ID del grupo
 * @param {string} [form.estado='programado'] - Estado inicial
 * @returns {Promise<Object>} Evento creado
 */
export async function createEvento(form) {
  const payload = clean({
    institucion_id: INSTITUCION_ID,
    colaborador_id: form.colaborador_id,
    nombre: form.nombre,
    tipo: form.tipo,
    observaciones: form.descripcion, // Mapear descripcion a observaciones
    fecha_inicio: form.fecha_inicio,
    fecha_fin: form.fecha_fin,
    ubicacion: form.ubicacion,
    grupo_id: form.grupo_id,
    estado: form.estado || "programado",
  });

  const res = await fetch(`${BASE_URL}/${INSTITUCION_ID}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...getAuthHeader(),
    },
    body: JSON.stringify(payload),
  });
  const data = await toJson(res);
  ensureOk(res, data);
  return data?.data ?? data;
}

/**
 * PUT - Actualizar un evento existente
 * Endpoint: PUT /api/eventos/:id
 * @param {Object} form - Datos a actualizar
 * @param {number} form.id - ID del evento (requerido)
 * @param {string} [form.nombre] - Nuevo nombre
 * @param {string} [form.tipo] - Nuevo tipo
 * @param {string} [form.descripcion] - Nueva descripción (se mapea a observaciones)
 * @param {string} [form.fecha_inicio] - Nueva fecha inicio
 * @param {string} [form.fecha_fin] - Nueva fecha fin
 * @param {string} [form.ubicacion] - Nueva ubicación
 * @param {string} [form.estado] - Nuevo estado
 * @param {number} [form.grupo_id] - Nuevo grupo
 * @returns {Promise<Object>} Evento actualizado
 */
export async function updateEvento(form) {
  if (!form.id) {
    throw new Error("El ID del evento es requerido para actualizar");
  }

  const payload = clean({
    institucion_id: INSTITUCION_ID,
    nombre: form.nombre,
    tipo: form.tipo,
    observaciones: form.descripcion, // Mapear descripcion a observaciones
    fecha_inicio: form.fecha_inicio,
    fecha_fin: form.fecha_fin,
    ubicacion: form.ubicacion,
    estado: form.estado,
    grupo_id: form.grupo_id,
    colaborador_id: form.colaborador_id,
  });

  const res = await fetch(`${BASE_URL}/${form.id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...getAuthHeader(),
    },
    body: JSON.stringify(payload),
  });
  const data = await toJson(res);
  ensureOk(res, data);
  return data?.data ?? data;
}

/**
 * DELETE - Eliminar un evento
 * Endpoint: DELETE /api/eventos/:id
 * @param {number} id - ID del evento a eliminar
 * @returns {Promise<Object>} Confirmación de eliminación
 */
export async function deleteEvento(id) {
  if (!id) {
    throw new Error("El ID del evento es requerido para eliminar");
  }

  const res = await fetch(`${BASE_URL}/${id}`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      ...getAuthHeader(),
    },
  });
  const data = await toJson(res);
  ensureOk(res, data);
  return data;
}

/**
 * Constantes para tipos y estados de eventos
 */
export const TIPOS_EVENTO = {
  ENTRENAMIENTO: "entrenamiento",
  TORNEO: "torneo",
  TODOS: "todos",
};

export const ESTADOS_EVENTO = {
  PROGRAMADO: "programado",
  EN_CURSO: "en_curso",
  FINALIZADO: "finalizado",
  CANCELADO: "cancelado",
  TODOS: "todos",
};
